import React from 'react';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import OptionsLoadingState from '../../components/options/OptionsLoadingState';
import { OptionsOnboardingCard } from '../../components/options/OptionsOnboardingCard';
import { useOptionsOverviewState } from '../../hooks/useOptionsOverviewState';
import { useOptionsHistoryData } from '../../hooks/useOptionsHistoryData';
import { OptionsHistoryControls } from '../../components/options/OptionsHistoryControls';
import { OptionsHistoryOperationCard } from '../../components/options/OptionsHistoryOperationCard';
import { OptionsHistoryCampaignCard } from '../../components/options/OptionsHistoryCampaignCard';
import { OptionsHistoryRollCard } from '../../components/options/OptionsHistoryRollCard';
import { OptionsPortfolioActivityCard } from '../../components/options/OptionsPortfolioActivityCard';
import { useOptionsMode } from '../../context/OptionsModeContext';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

export default function Operations() {
  const { isDemoMode } = useOptionsMode();
  const { t } = useOptionsLocale();
  const { connected, loading: overviewLoading, reloadOverview, overview } = useOptionsOverviewState();
  const {
    loading,
    syncing,
    currentView,
    setCurrentView,
    selectedCurrency,
    setSelectedCurrency,
    selectedRange,
    setSelectedRange,
    customRange,
    setCustomRange,
    handleApplyCustomRange,
    underlyingFilter,
    setUnderlyingFilter,
    strategyFilter,
    setStrategyFilter,
    strategyOptions,
    statusFilter,
    setStatusFilter,
    sortMode,
    setSortMode,
    filteredOperations,
    sortedOperations,
    filteredCampaigns,
    filteredActivities,
    rollEvents,
    campaignMapByOperationId,
    operationDetails,
    campaignDetails,
    ensureOperationDetail,
    ensureCampaignDetail,
    loadingOperationId,
    loadingCampaignId,
    handleSync,
  } = useOptionsHistoryData(connected, reloadOverview);

  const emptyState = {
    operations: {
      title: t('history.emptyOperationsTitle'),
      description: t('history.emptyOperationsDescription'),
    },
    campaigns: {
      title: t('history.emptyCampaignsTitle'),
      description: t('history.emptyCampaignsDescription'),
    },
    rolls: {
      title: t('history.emptyRollsTitle'),
      description: t('history.emptyRollsDescription'),
    },
  };

  const contentByView = {
    operations: {
      items: filteredOperations,
      empty: emptyState.operations,
    },
    campaigns: {
      items: filteredCampaigns,
      empty: emptyState.campaigns,
    },
    rolls: {
      items: rollEvents,
      empty: emptyState.rolls,
    },
  };

  const currentContent = contentByView[currentView];

  return (
    <OptionsPageShell
      title={t('history.title')}
      description={t('history.description')}
      eyebrow={t('history.eyebrow')}
      hideHeader
    >
      {overviewLoading ? (
        <OptionsLoadingState
          title={t('common.connectDeribit')}
          description={t('history.description')}
          testId="options-history-loading-overview"
        />
      ) : !connected ? (
        <OptionsOnboardingCard
          title={t('onboarding.title')}
          description={t('onboarding.description')}
          connectLabel={t('common.connectDeribit')}
          securityTitle={t('onboarding.securityTitle')}
          securityBody={t('onboarding.securityBody')}
          securityDetails={t('onboarding.securityDetails')}
        />
      ) : loading ? (
        <OptionsLoadingState
          title={t('common.syncingNow')}
          description={t('history.description')}
          testId="options-history-loading-state"
        />
      ) : (
        <div className="space-y-6" data-testid="options-history-root">
          <OptionsHistoryControls
            currentView={currentView}
            onViewChange={setCurrentView}
            selectedCurrency={selectedCurrency}
            onCurrencyChange={setSelectedCurrency}
            selectedRange={selectedRange}
            onRangeChange={setSelectedRange}
            customRange={customRange}
            onCustomRangeChange={setCustomRange}
            onApplyCustomRange={handleApplyCustomRange}
            underlyingFilter={underlyingFilter}
            onUnderlyingChange={setUnderlyingFilter}
            strategyFilter={strategyFilter}
            onStrategyChange={setStrategyFilter}
            strategyOptions={strategyOptions}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            sortMode={sortMode}
            onSortChange={setSortMode}
            syncing={syncing}
            onSync={handleSync}
            showSyncButton={!isDemoMode && overview?.can_sync !== false}
          />

          {!currentContent.items.length && !(currentView === 'operations' && filteredActivities.length) ? (
            <div className="rounded-[32px] border border-dashed border-white/10 bg-white/[0.03] px-6 py-12 text-center" data-testid={`options-history-empty-${currentView}`}>
              <p className="text-2xl font-black font-heading text-white" data-testid={`options-history-empty-title-${currentView}`}>{currentContent.empty.title}</p>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-white/60 sm:text-base" data-testid={`options-history-empty-description-${currentView}`}>{currentContent.empty.description}</p>
            </div>
          ) : currentView === 'operations' ? (
            <div className="space-y-4" data-testid="options-history-operations-list">
              {sortedOperations.map((operation, index) => (
                <OptionsHistoryOperationCard
                  key={operation.id}
                  operation={operation}
                  index={index}
                  selectedCurrency={selectedCurrency}
                  campaign={campaignMapByOperationId.get(operation.id) || null}
                  detail={operationDetails[operation.id] || null}
                  onExpand={ensureOperationDetail}
                  loadingDetail={loadingOperationId === operation.id}
                />
              ))}
              {filteredActivities.length ? (
                <div className="space-y-4 pt-2" data-testid="options-history-management-activities-section">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45" data-testid="options-history-management-activities-title">{t('history.managementActivitiesTitle')}</p>
                    <p className="text-sm text-white/55" data-testid="options-history-management-activities-description">{t('history.managementActivitiesDescription')}</p>
                  </div>
                  {filteredActivities.map((item, index) => (
                    <OptionsPortfolioActivityCard key={item.id} item={item} index={index} />
                  ))}
                </div>
              ) : null}
            </div>
          ) : currentView === 'campaigns' ? (
            <div className="space-y-4" data-testid="options-history-campaigns-list">
              {filteredCampaigns.map((campaign, index) => (
                <OptionsHistoryCampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  detail={campaignDetails[campaign.id] || null}
                  index={index}
                  selectedCurrency={selectedCurrency}
                  onExpand={ensureCampaignDetail}
                  loadingDetail={loadingCampaignId === campaign.id}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4" data-testid="options-history-rolls-list">
              {rollEvents.map((event, index) => (
                <OptionsHistoryRollCard key={event.key} event={event} index={index} selectedCurrency={selectedCurrency} />
              ))}
            </div>
          )}
        </div>
      )}
    </OptionsPageShell>
  );
}